/**
 * The retainer: the package, its allowances, its package changes and its content
 * plans.
 *
 * Two of these are approvals and both carry `money: true` — `useApiMutation` adds
 * the `Idempotency-Key` the route's own `idempotent()` middleware requires, and
 * refuses the call under impersonation before it is sent
 * (`security/impersonation.ts` refuses it again on the server).
 *
 * A content-plan sign-off is **not** money (`.docs/00-index.md` §6: "No money.
 * Owner/admin sign off"), so it carries no key — and the route has no
 * `idempotent()` for it either.
 */

import type { OveragePolicy } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'
import type { DetailOptions } from './requests.js'

/** `GET /api/clients/me/retainer` — `null` for a client on prepay widgets only. */
export function useRetainer() {
  return useApiQuery({
    key: queryKeys.retainers.retainer(),
    request: (api) => api.rest.clients.me.retainer.$get(),
  })
}

/**
 * `GET /api/clients/me/retainer/allowances?period=` — the meters.
 *
 * `inline` by default, and that is not caution: the route answers **404** for a
 * client with no retainer (`requireRetainer`), so a page that reads it without the
 * opt-out replaces itself with an error boundary for every prepay client. The
 * allowances region is absent for them, which is the session file's own edge case.
 */
export function useAllowances(period?: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.retainers.allowances(period),
    request: (api) =>
      api.rest.clients.me.retainer.allowances.$get({
        query: period === undefined ? {} : { period },
      }),
    inline: options.inline ?? true,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  })
}

/** `GET /api/clients/me/retainer/changes` — every `RET-CHG-NNN`, decided or not. */
export function useRetainerChanges() {
  return useApiQuery({
    key: queryKeys.retainers.changes(),
    request: (api) => api.rest.clients.me.retainer.changes.$get(),
  })
}

/** `POST …/retainer/changes/:ref/approve` — the contract event for a package. */
export function useApproveRetainerChange() {
  return useApiMutation({
    module: 'retainers',
    money: true,
    request: (api, ref: string, options) =>
      api.rest.clients.me.retainer.changes[':ref'].approve.$post(
        { param: { ref } },
        { headers: options.headers },
      ),
    alsoInvalidates: ['core'],
  })
}

/** `POST …/retainer/changes/:ref/decline` — nothing is charged and nothing changes. */
export function useDeclineRetainerChange() {
  return useApiMutation({
    module: 'retainers',
    request: (api, variables: { ref: string; note: string }) =>
      api.rest.clients.me.retainer.changes[':ref'].decline.$post({
        param: { ref: variables.ref },
        json: { note: variables.note },
      }),
    alsoInvalidates: ['core'],
  })
}

/**
 * `POST …/retainer/items/:service/overage-policy` — the standing approval.
 *
 * `money: true`: `rolling` is the client agreeing in advance to be billed for work
 * past an allowance (`SERVICES.md` §Retainers), which is exactly what the money
 * rules are about, and the route carries `idempotent()` for it.
 */
export function useSetOveragePolicy() {
  return useApiMutation({
    module: 'retainers',
    money: true,
    request: (api, variables: { service: string; policy: OveragePolicy }, options) =>
      api.rest.clients.me.retainer.items[':service']['overage-policy'].$post(
        { param: { service: variables.service }, json: { policy: variables.policy } },
        { headers: options.headers },
      ),
  })
}

/** `GET /api/clients/me/deliverables?period=` — what was delivered, month by month. */
export function useDeliverables(period?: string) {
  return useApiQuery({
    key: queryKeys.retainers.deliverables(period),
    request: (api) =>
      api.rest.clients.me.deliverables.$get({
        query: period === undefined ? {} : { period },
      }),
  })
}

/** `GET /api/clients/me/content-plans` — every `PLAN-YYYY-MM` this client has. */
export function useContentPlans() {
  return useApiQuery({
    key: queryKeys.retainers.contentPlans(),
    request: (api) => api.rest.clients.me['content-plans'].$get(),
  })
}

/** `POST …/content-plans/:ref/sign-off` — releases a month of work into production. */
export function useSignOffContentPlan() {
  return useApiMutation({
    module: 'retainers',
    request: (api, ref: string) =>
      api.rest.clients.me['content-plans'][':ref']['sign-off'].$post({ param: { ref } }),
    alsoInvalidates: ['core'],
  })
}

/** `POST …/content-plans/:ref/request-changes` — what should change, in words. */
export function useRequestPlanChanges() {
  return useApiMutation({
    module: 'retainers',
    request: (api, variables: { ref: string; note: string }) =>
      api.rest.clients.me['content-plans'][':ref']['request-changes'].$post({
        param: { ref: variables.ref },
        json: { note: variables.note },
      }),
    alsoInvalidates: ['core'],
  })
}
